import { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, ChevronDown, Check, User, LogOut, WifiOff, RefreshCw, CloudOff, FlaskConical, X } from 'lucide-react';
import { useAuth } from '../auth/AuthContext.js';
import { getInitialen } from '../shared/utils/formatters.js';
import { useModuleLabels } from '../shared/hooks/useModuleLabels.js';
import { useOfflineSync } from '../shared/hooks/useOfflineSync.js';
import { NotificatieBell } from '../../modules/notificaties/components/NotificatieBell.js';
import { GlobalSearch } from '../shared/components/GlobalSearch.js';
import { useBrand } from '../shared/components/BrandContext.js';
import { KordaroLogo, KordaroMark } from '../shared/components/KordaroLogo.js';

export function Topbar() {
  const { user, activeEntity, accessibleEntities, dataScope, switchEntity, logout } = useAuth();
  const brand = useBrand();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const labels = useModuleLabels();
  const { isOnline, pendingCount, isSyncing, triggerSync } = useOfflineSync();

  // Derive page title from route — uses configurable module labels
  const pageTitle = useMemo(() => {
    const p = location.pathname;
    if (p.startsWith('/dashboard')) return 'Dashboard';
    if (p.startsWith('/winkel')) return labels.winkel.label;
    if (p.startsWith('/dossiers')) return labels.buitendienst.label;
    if (p.startsWith('/crm')) return labels.crm.label;
    if (p.startsWith('/logistiek')) return labels.logistiek.label;
    if (p.startsWith('/communicatie')) return labels.communicatie.label;
    if (p.startsWith('/planning')) return labels.planning.label;
    if (p.startsWith('/settings')) return 'Instellingen';
    if (p.startsWith('/notificaties')) return 'Notificaties';
    if (p.startsWith('/buitendienst')) return labels.buitendienst.label;
    if (p.startsWith('/vorderingsstaten')) return 'Vorderingsstaat';
    return brand.naam;
  }, [location.pathname, labels, brand.naam]);

  // Cmd+K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(o => !o);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Close user menu on outside click
  useEffect(() => {
    if (!userMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [userMenuOpen]);

  const hasMultipleEntities = accessibleEntities.length > 1;

  // Demo banner: compute days remaining from demoExpiresAt
  const [demoBannerHidden, setDemoBannerHidden] = useState(() =>
    localStorage.getItem('demoBannerHidden') === 'true'
  );
  const demoInfo = useMemo(() => {
    if (!activeEntity?.isDemo) return null;
    const expiresAt = activeEntity.demoExpiresAt ? new Date(activeEntity.demoExpiresAt) : null;
    if (!expiresAt) return { daysRemaining: null };
    const days = Math.max(0, Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
    return { daysRemaining: days };
  }, [activeEntity]);

  const handleSwitch = async (entityId: string) => {
    if (entityId === activeEntity?.id) { setUserMenuOpen(false); return; }
    setUserMenuOpen(false);
    try { await switchEntity(entityId); } catch { /* entity switch failed silently */ }
  };

  return (
    <>
      {/* Demo account banner */}
      {demoInfo && !demoBannerHidden && (
        <div className="bg-amber-500 text-white text-xs font-medium flex items-center justify-between px-4 py-1.5 flex-shrink-0">
          <div className="flex items-center gap-2">
            <FlaskConical size={13} />
            <span>
              Dit is een <strong>demo omgeving</strong>.
              {demoInfo.daysRemaining !== null && (
                <> Nog <strong>{demoInfo.daysRemaining} dag{demoInfo.daysRemaining !== 1 ? 'en' : ''}</strong> geldig.</>
              )}
            </span>
          </div>
          <button
            onClick={() => { setDemoBannerHidden(true); localStorage.setItem('demoBannerHidden', 'true'); }}
            className="text-white/70 hover:text-white transition-colors ml-4"
            title="Verberg banner"
          >
            <X size={13} />
          </button>
        </div>
      )}

      <header className="focus-on-dark h-[44px] md:h-[52px] bg-gold flex items-center justify-between px-4 md:px-5 flex-shrink-0 z-50">
        {/* Left: platformlogo (enkel zonder eigen winkelbranding) + page title + entity name */}
        <div className="flex items-center gap-2.5">
          {brand.toontPlatformMerk && (
            <>
              <KordaroLogo variant="ivory" className="hidden md:block mr-2" />
              <KordaroMark variant="ivory" className="md:hidden" />
              <span className="hidden md:block text-white/40 text-sm" aria-hidden="true">|</span>
            </>
          )}
          <span className="text-white font-semibold text-base md:text-lg hidden md:block">{pageTitle}</span>
          {!brand.toontPlatformMerk && <span className="text-white text-sm md:hidden" aria-hidden="true">◆</span>}
          {activeEntity && (
            <>
              <span className="hidden md:block text-white/40 text-sm">|</span>
              <span className="hidden md:block text-white/80 text-sm font-medium">{activeEntity.name}</span>
            </>
          )}
        </div>

        {/* Right: Search + Notifications + User */}
        <div className="flex items-center gap-3 md:gap-4">
          {/* Search trigger */}
          <button
            onClick={() => setSearchOpen(true)}
            className="text-white/70 hover:text-white transition-colors"
            title="Zoeken (Ctrl+K)"
          >
            <Search size={16} className="md:w-[18px] md:h-[18px]" />
          </button>

          {/* Offline indicator + sync queue status */}
          {!isOnline && (
            <div className="flex items-center gap-1.5 text-white/90" title="Geen internetverbinding">
              <WifiOff size={15} />
              {pendingCount > 0 && (
                <span className="text-[10px] font-bold bg-white/20 px-1.5 py-0.5 rounded-full">
                  {pendingCount}
                </span>
              )}
            </div>
          )}
          {isOnline && pendingCount > 0 && (
            <button
              onClick={triggerSync}
              className="flex items-center gap-1.5 text-white/90 hover:text-white transition-colors"
              title={`${pendingCount} wijziging${pendingCount !== 1 ? 'en' : ''} synchroniseren`}
            >
              {isSyncing ? (
                <RefreshCw size={15} className="animate-spin" />
              ) : (
                <CloudOff size={15} />
              )}
              <span className="text-[10px] font-bold bg-white/20 px-1.5 py-0.5 rounded-full">
                {pendingCount}
              </span>
            </button>
          )}

          {/* Notifications */}
          <NotificatieBell />

          {/* User dropdown */}
          <div ref={menuRef} className="relative">
            <button
              onClick={() => setUserMenuOpen(o => !o)}
              className="flex items-center gap-1.5 md:gap-2"
            >
              {user && (
                <div className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-white text-gold flex items-center justify-center text-[10px] md:text-[11px] font-bold">
                  {getInitialen(user.firstName, user.lastName)}
                </div>
              )}
              <ChevronDown size={12} className="text-white/60" />
            </button>

            {userMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
                {/* User info */}
                {user && (
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-900">{user.firstName} {user.lastName}</p>
                    <p className="text-xs text-gray-400">{user.email}</p>
                  </div>
                )}

                {/* Entity section */}
                <div className="border-b border-gray-100">
                  <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider px-4 pt-2 pb-1">Winkel</p>
                  {accessibleEntities.map(entity => (
                    <button
                      key={entity.id}
                      onClick={() => hasMultipleEntities ? handleSwitch(entity.id) : undefined}
                      className={`w-full flex items-center justify-between px-4 py-2 text-left transition-colors ${hasMultipleEntities ? 'hover:bg-gray-50 cursor-pointer' : ''}`}
                    >
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-gray-900 truncate block">{entity.name}</span>
                        {entity.dataScope === 'sales_only' && (
                          <span className="text-[9px] bg-yellow-100 text-yellow-700 px-1 py-0.5 rounded font-medium">sales only</span>
                        )}
                      </div>
                      {entity.id === activeEntity?.id && (
                        <Check size={14} className="text-gold flex-shrink-0 ml-2" />
                      )}
                    </button>
                  ))}
                </div>

                {/* Actions */}
                <div className="py-1">
                  <button
                    onClick={() => { navigate('/settings'); setUserMenuOpen(false); }}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <User size={15} className="text-gray-400" />
                    Profiel
                  </button>
                  <button
                    onClick={() => { setUserMenuOpen(false); logout(); }}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut size={15} className="text-red-400" />
                    Uitloggen
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Global search overlay */}
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
